/**
 * Language for the console and the phone: English (the default) or Spanish. The Spanish
 * dictionary is large (the design's whole sweep), so it loads only when Spanish is picked:
 * loadSpanish() once, then spanish() answers synchronously.
 */
import { createEsEngine, type EsEngine } from './engine';
import { installExtras } from './es-extra';
import { installPhone } from './es-phone';
import { installCapture } from './es-capture';
import { installGuide } from './es-guide';
import { installHelpFaq } from './es-help-faq';
import { installHelpDocs } from './es-help-docs';
import { __resetNamesForTests, flushNames, setNameSink } from './names';

export { harvestNames, noteAnswer, protectNames } from './names';

export type { EsEngine, EsRule } from './engine';
export { createEsEngine } from './engine';

export type Lang = 'en' | 'es';

export const LANGS: readonly Lang[] = ['en', 'es'];

/** A value from storage or the server, as a language (anything else is English). */
export function asLang(v: unknown): Lang {
  return v === 'es' ? 'es' : 'en';
}

/** The note under the language switch, in English (the dictionary has its Spanish).
 *  Only what is true: the screens follow the pick; what Atlas writes, alerts, texts and
 *  anything sent to a family or a doctor arrive as the server wrote them. */
export const LANG_NOTE =
  'Screens in the app use this language. Atlas replies, alerts, texts and messages to families or doctors are not translated: they arrive as they were written.';

/** Shown under the language switch only, never on a care screen. */
export const NURSE_REVIEW_NOTE = 'Clinical Spanish pending nurse review';

let engine: EsEngine | null = null;
let loading: Promise<EsEngine> | null = null;
const listeners = new Set<() => void>();

/** The Spanish engine once loaded, else null. */
export function spanish(): EsEngine | null {
  return engine;
}

/** The dictionary module (es-data.ts). */
export type EsData = { default: (A: EsEngine) => void };
// The web console loads it on demand (its own chunk). An app whose bundler or test runner
// cannot load a module on demand hands it over with setSpanishSource (the phone does).
let source: () => EsData | Promise<EsData> = () => import('./es-data');

export function setSpanishSource(s: () => EsData | Promise<EsData>): void {
  source = s;
}

/** Load the dictionary (once). */
export function loadSpanish(): Promise<EsEngine> {
  if (engine) return Promise.resolve(engine);
  if (!loading) {
    loading = Promise.resolve(source()).then((mod) => {
      const e = createEsEngine();
      (mod.default as (a: EsEngine) => void)(e);
      installExtras(e);
      installPhone(e);
      installCapture(e);
      installGuide(e);
      installHelpFaq(e);
      installHelpDocs(e);
      engine = e;
      // From now on names go straight to the engine, starting with the recent answers.
      setNameSink((names) => e.protectNames(names));
      flushNames();
      listeners.forEach((l) => l());
      return e;
    });
  }
  return loading;
}

/** Called when the dictionary has loaded (for screens to draw again). */
export function onSpanishReady(l: () => void): () => void {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}

/** Test seam: forget the loaded engine and protected names. */
export function __resetSpanishForTests(): void {
  engine = null;
  loading = null;
  __resetNamesForTests();
}
